import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  GridComponent,
  GridDataResult,
  PageChangeEvent,
} from '@progress/kendo-angular-grid';
import {
  CompositeFilterDescriptor,
  process,
  SortDescriptor,
  State,
} from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';
import { LoaderService } from '../../../core/services/loader.service';
import { ReadingsService } from '../../../core/services/readings.services';
import { SweetAlertIcon } from '../../../shared/constants/enum';
import { table_pageSize } from '../../../shared/modals/common';
import { SharedService } from '../../../shared/shared.service';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { Router } from '@angular/router';

@Component({
  selector: 'app-ev-readings',
  templateUrl: './ev-readings.component.html',
  styleUrl: './ev-readings.component.scss',
})
export class EvReadingsComponent implements OnInit, OnDestroy {
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective | undefined;
  @ViewChild('exportEvReadings') public exportEvReadings:
    | GridComponent
    | undefined;
  @ViewChild('exportEvRawData') public exportEvRawData:
    | GridComponent
    | undefined;

  breadCrumbItems: Array<{}> = [{ label: 'EV Readings', url: 'evReadings' }];

  evReadingsList: any[] = [];
  selectedEvReadings: any[] = [];
  evReadingsGridData: GridDataResult = { data: [], total: 0 };

  evRawDataList: any[] = [];
  selectedEvRawData: any[] = [];
  pageSize = 10;
  skip = 0;
  currentPage = 1;
  totalCount = 0;
  buttonCount = 5;
  info = true;
  type: 'numeric' | 'input' = 'numeric';
  pageSizes = table_pageSize.pageSizes;
  previousNext = true;
  selectableSettings = this.sharedService.selectableSettings;

  sort: SortDescriptor[] = [];
  orderByQuery = '';
  queryString = '';
  evReadingsSearchValue = '';
  state: State = {
    skip: 0,
    take: 10,
    filter: {
      logic: 'and',
      filters: [],
    },
  };

  private evReadingsSubscription = new Subscription();
  private evRawDataSubscription = new Subscription();

  constructor(
    private loaderService: LoaderService,
    private readingsService: ReadingsService,
    private sharedService: SharedService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.getEvReadings();
  }

  ngOnDestroy(): void {
    this.evReadingsSubscription.unsubscribe();
    this.evRawDataSubscription.unsubscribe();
  }

  onExport(type: string): void {
    if (type === 'Excel') {
      setTimeout(() => {
        this.exportEvReadings?.saveAsExcel();
      }, 500);
    }
  }

  onRawExport(type: string): void {
    if (type === 'Excel') {
      setTimeout(() => {
        this.exportEvRawData?.saveAsExcel();
      }, 500);
    }
  }

  onSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.state.sort = sort;
    this.orderByQuery = this.sharedService.createOrderByQuery(
      this.mapSortFields(sort),
    );
    this.getEvReadings();
  }

  onFilterChange(filter: CompositeFilterDescriptor): void {
    this.state.filter = filter;
    this.state.skip = 0;
    this.skip = 0;
    this.currentPage = 1;
    this.queryString = this.sharedService.createQuery({
      ...this.state,
      filter: this.mapFilterFields(filter),
    });
    this.getEvReadings();
  }

  pageChange({ skip, take }: PageChangeEvent): void {
    this.skip = skip;
    this.pageSize = take;
    this.state.skip = skip;
    this.state.take = take;
    this.currentPage = Math.floor(skip / take) + 1;
    this.getEvReadings();
  }

  selectionChanged(selection: any): void {
    this.updateSelection(selection, this.selectedEvReadings);
  }

  clearSearchValueChange(ev: string): void {
    const trimmedValue = ev.trim();

    if (trimmedValue.length === 0 && ev.length > 0) {
      return;
    }

    if (ev.length === 0) {
      this.pageChange({ skip: 0, take: this.pageSize });
    }
  }

  onSearch(): void {
    this.triggerSearch();
  }

  onFilter(value: Event): void {
    this.triggerSearch();
  }

  navigateToViewReadings(readings: any, type: string): void {
    const serializedData = JSON.stringify(readings);
    this.router.navigate(['/evReadings/viewDetails'], {
      state: { readings: serializedData, type },
    });
  }

  showTooltip(e: MouseEvent): void {
    const element = e.target as HTMLElement;

    if (element.nodeName === 'TD') {
      const cellValue = element.innerText.trim();

      if (cellValue && !element.closest('.no-tooltip')) {
        this.tooltipDir?.toggle(element);
      } else {
        this.tooltipDir?.hide();
      }
    } else if (element.classList.contains('k-column-title')) {
      this.tooltipDir?.toggle(element);
    } else {
      this.tooltipDir?.hide();
    }
  }

  private getEvReadings(): void {
    this.loaderService.setLoading(true);
    this.evReadingsSubscription.unsubscribe();
    this.evReadingsSubscription = this.readingsService
      .getEvReadings(
        this.currentPage,
        this.pageSize,
        this.evReadingsSearchValue,
        this.queryString,
        this.orderByQuery,
      )
      .subscribe({
        next: (res: any) => {
          const responseData = res?.data ?? res;
          const items = responseData?.items ?? [];

          this.evReadingsList = items.map((item: any, index: number) =>
            this.normalizeEvReading(item, index, this.currentPage),
          );
          this.pageSize = responseData?.pageSize ?? this.pageSize;
          this.totalCount = responseData?.totalCount ?? items.length;
          this.loadEvReadings();
          this.loaderService.setLoading(false);
        },
        error: (err) => this.handleError(err),
      });
  }

  private loadEvReadings(): void {
    const processedData = process(this.evReadingsList, {
      take: this.pageSize,
      sort: this.sort,
      filter: {
        logic: 'and',
        filters: [],
      } as CompositeFilterDescriptor,
    });

    this.evReadingsGridData = {
      data: processedData.data,
      total: this.totalCount,
    };
  }

  private triggerSearch(): void {
    const trimmedValue = this.evReadingsSearchValue.trim();

    if (trimmedValue.length === 0 && this.evReadingsSearchValue.length > 0) {
      return;
    }

    if (this.evReadingsSearchValue.length === 0) {
      this.pageChange({ skip: 0, take: this.pageSize });
      return;
    }

    this.getEvReadings();
  }

  private normalizeEvReading(item: any, index: number, page: number): any {
    const rowId = this.firstAvailableValue([
      item.id,
      item.evGatewayProviderJsonId,
      item.evGatewayProviderJSONId,
    ]);

    return {
      ...item,
      id: rowId ?? `${page}-${index}`,
      utilityType: item.utilityType ?? item.utility ?? '-',
      deviceSRNO: this.displayValue(
        item.deviceSRNO ?? item.deviceSrNo ?? item.devicesSRNO,
      ),
      macAddress: this.displayValue(item.macAddress ?? item.deviceAddress),
      startDate: item.startDate ?? item.StartDate ?? '-',
      endDate: item.endDate ?? item.EndDate ?? '-',
      unitRate: this.displayValue(item.unitRate),
      amount: this.displayValue(item.amount),
      noOfUnits: this.displayValue(item.noOfUnits),
      projectName: this.displayValue(item.projectName),
      evGatewayProviderJsonId: item.evGatewayProviderJsonId ?? item.evGatewayProviderJSONId ?? '-',
      rawJson:
        item.rawJson ??
        null,
      convertedJson:
        item.convertedJson ??
        null,
    };
  }

  private firstAvailableValue(values: any[]): any {
    return values.find(
      (value) => value !== null && value !== undefined && value !== '',
    );
  }

  private displayValue(value: any): any {
    return value === null || value === undefined || value === '' ? '-' : value;
  }

  private mapSortFields(sort: SortDescriptor[]): SortDescriptor[] {
    return sort.map((descriptor) => ({
      ...descriptor,
      field: this.toApiField(descriptor.field),
    }));
  }

  private mapFilterFields(
    filter: CompositeFilterDescriptor,
  ): CompositeFilterDescriptor {
    return {
      ...filter,
      filters: filter.filters.map((item: any) => {
        if (item.filters) {
          return this.mapFilterFields(item);
        }

        return {
          ...item,
          field: this.toApiField(item.field),
        };
      }),
    };
  }

  private toApiField(field: string): string {
    const fieldMap: Record<string, string> = {
      date_Time: 'receivedTime',
      deviceAddress: 'deviceAddress',
    };

    return fieldMap[field] ?? field;
  }

  private updateSelection(selection: any, selectedRows: any[]): void {
    selection.deselectedRows.forEach((row: any) => {
      const index = selectedRows.findIndex(
        (selectedRow: any) => selectedRow.id === row.dataItem.id,
      );
      if (index > -1) {
        selectedRows.splice(index, 1);
      }
    });

    selection.selectedRows.forEach((row: any) => {
      const alreadySelected = selectedRows.some(
        (selectedRow: any) => selectedRow.id === row.dataItem.id,
      );

      if (!alreadySelected) {
        selectedRows.push(row.dataItem);
      }
    });
  }

  private handleError(err: any): void {
    this.loaderService.setLoading(false);
    const errorMsg =
      err.error?.message || 'Something went wrong. Please try again later.';

    this.sharedService.showMessageDialog(errorMsg, '', SweetAlertIcon.ERROR);
  }
}
